import {
  Alert,
  Box
} from "@mui/material";
import { GridSortDirection, GridSortModel } from "@mui/x-data-grid-premium";
import { useSnackbar } from "notistack";
import React from "react";
import { useNavigate } from "react-router-dom";
import { CustomDataGrid, DeletePanel, MyPaper, TitleHeader } from "../../components";
import { SelectBoxOption, TagInfo } from "../../interfaces";
import { useAppDispatch } from "../../redux";
import { useCreateDataTableMutation, useDeleteDataTableMutation, useGetTagsBasedOnParameterQuery, useLazyGetAllDataTableQuery, useUpdateDataTableMutation } from "../../redux/services";
import { closeBackdrop, openBackdrop } from "../../redux/slices/backdrop";
import {
  closeConfirmationDialog,
  openConfirmationDialog,
} from "../../redux/slices/confirmationDialog";
import { generateDataTableFormat } from "../../utils";
import { allOption } from "../checkList";
import { TableColumn } from "./utils";

const InputParameterList = ({ hasAccess }: { hasAccess: boolean }) => {

  const dispatch = useAppDispatch();

  const tagsData = useGetTagsBasedOnParameterQuery("tech_para", { refetchOnMountOrArgChange: true, refetchOnFocus: true });
  const [
    getAllInputParameter,
    {
      data: allData = { data: [], totalCount: 0 },
      isFetching,
      isLoading,
      isError,
    },
  ] = useLazyGetAllDataTableQuery({
    refetchOnFocus: true,
  });
  const { data, totalCount } = allData;

  const loading = isLoading || isFetching;

  const [search, setSearch] = React.useState("");
  const [selectedTag, setTag] = React.useState<SelectBoxOption>(allOption);
  const [paginationModel, setPaginationModel] = React.useState({
    page: 0,
    pageSize: 100,
  });
  const [queryOptions, setQueryOptions] = React.useState({
    sortModel: [{ field: 'sequence_id', sort: 'desc' as GridSortDirection }],
  });

  const [createDT] = useCreateDataTableMutation();
  const [updateDT] = useUpdateDataTableMutation();

  const [deleteTechnicalParameterMutation] = useDeleteDataTableMutation();

  const { enqueueSnackbar } = useSnackbar();

  const navigate = useNavigate();

  const giveMeRows = () => {
    if (!loading && data) {
      return data ?? [];
    } else {
      return [];
    }
  }

  let ROWS = giveMeRows();

  const deleteInputParameter = (input: any) => {
    dispatch(
      openConfirmationDialog({
        title: "Delete Operation",
        body: `Are you sure you want to delete the data table ${input.sequence_id}`,
        positiveBtn: "Delete",
        negativeBtn: "Cancel",
        onOk: () => performRowDel(input._id),
        onNegativeBtn: () => dispatch(closeConfirmationDialog()),
      })
    );
  };

  const performRowDel = async (_id: string) => {
    dispatch(openBackdrop("Deleting data table template..."));
    let res: any = {};
    try {
      res = await deleteTechnicalParameterMutation({
        dtTemplateId: _id,
        dtTemplateType: "inputParameter",
        dtInstanceType: "template"
      });

      if (Object.keys(res).includes("data")) {
        enqueueSnackbar(`Deleted data table template Successfully!`, { variant: "success" });
        dispatch(closeConfirmationDialog());
        fetchInputParameters();
      } else if (Object.keys(res).includes("error")) {
        // eslint-disable-next-line
        throw res.error.data;
      } else {
        // eslint-disable-next-line
        throw "Data not found";
      }
    } catch (error: any) {
      let errorMessage: string = error?.title ?? "Oops! Something went wrong, Unable to delete data table template";
      enqueueSnackbar(errorMessage, { variant: "error" });
    } finally {
      dispatch(closeBackdrop());
    }
  };

  const viewInputParameter = (data: any) => {
    if (hasAccess) {
      navigate(`${window.location.pathname}/${data?._id || "New"}`);
    }
  };

  const getPath = (id: string) => `${window.location.pathname}/${id}`;

  const duplicate = (data: any) => {
    let message = `Do you want to create a copy of this "${data.sequence_id}" data table?`;
    dispatch(
      openConfirmationDialog({
        title: "Are you sure?",
        body: message,
        positiveBtn: "Create",
        negativeBtn: "Cancel",
        onOk: () => performDuplicate(data),
        onNegativeBtn: () => dispatch(closeConfirmationDialog()),
      })
    );
  };

  const performDuplicate = async (data: any) => {
    dispatch(openBackdrop(`Creating copy of "${data.sequence_id}" data table....`));
    let res: any = {};
    try {
      const extractedDigit = Math.abs(data?.name?.match(/-\d+/)?.at(-1)) || 0;

      const parameterName = `${extractedDigit ? data?.name?.replace(/-\d/, '') : data.name}-${(Number(extractedDigit) ?? 1) + 1}`;

      res = await createDT({
        payload: {
          ...data, name: parameterName, tags_info: data?.tags_info?.map((tag: TagInfo) => ({
            tag_group_id: tag?.tag_group_id,
            tag_id: tag?.tag_id
          })),
          tables: generateDataTableFormat(data?.tables)
        },
        dtTemplateType: "inputParameter",
        dtInstanceType: "template",
        dtTemplateId: null,
        isDuplicating: true
      });
      if (Object.keys(res).includes("data")) {
        enqueueSnackbar(
          `Created copy of ${res?.data?.sequence_id} Successfully!`,
          { variant: "success" }
        );
        dispatch(closeConfirmationDialog());
        fetchInputParameters();
      } else if (Object.keys(res).includes("error")) {
        // eslint-disable-next-line
        throw "Unable to create copy";
      } else {
        // eslint-disable-next-line
        throw "Data not found";
      }
    } catch (error) {
      let errorMessage: string = `Unable to create copy of ${data.sequence_id}!`;
      enqueueSnackbar(errorMessage, { variant: "error" });
      dispatch(closeBackdrop());
    } finally {
      dispatch(closeBackdrop());
      dispatch(closeConfirmationDialog());
    }
  };

  const updatePublishStatus = (row: any) => {
    dispatch(
      openConfirmationDialog({
        title: "Publish Operation",
        body: <DeletePanel
          message={`Are you sure you want to publish the Data Table ${row?.sequence_id}?`}
          warningMessage={`You will not be able to edit this after publishing.`}
        />,
        positiveBtn: "Publish",
        negativeBtn: "Cancel",
        onOk: () => onPublish(row),
        onNegativeBtn: () => dispatch(closeConfirmationDialog())
      })
    );
  };

  const onPublish = async (data: any) => {
    dispatch(openBackdrop(`Publishing data table "${data.sequence_id}" ....`));
    let res: any = {};
    try {
      res = await updateDT({
        payload: {
          ...data, name: data?.name, tags_info: data?.tags_info?.map((tag: TagInfo) => ({
            tag_group_id: tag?.tag_group_id,
            tag_id: tag?.tag_id
          })),
          tables: generateDataTableFormat(data?.tables),
          status: "Published"
        },
        dtTemplateType: "inputParameter",
        dtInstanceType: "template",
        dtTemplateId: data?._id,
        publish: true
      });
      if (Object.keys(res).includes("data")) {
        enqueueSnackbar(
          `Published ${res?.data?.sequence_id} Successfully!`,
          { variant: "success" }
        );
        dispatch(closeConfirmationDialog());
        fetchInputParameters();
      } else if (Object.keys(res).includes("error")) {
        // eslint-disable-next-line
        throw "Unable to publish";
      } else {
        // eslint-disable-next-line
        throw "Data not found";
      }
    } catch (error) {
      let errorMessage: string = `Unable to publish ${data.sequence_id}!`;
      enqueueSnackbar(errorMessage, { variant: "error" });
      dispatch(closeBackdrop());
    } finally {
      dispatch(closeBackdrop());
      dispatch(closeConfirmationDialog());
    }
  };


  const handleSortModelChange = React.useCallback((sortModel: GridSortModel) => {
    // Here you save the data you need from the sort model
    setQueryOptions({ sortModel: [...sortModel] });
  }, [])

  const fetchInputParameters = () => {
    getAllInputParameter({
      ...paginationModel,
      dtTemplateType: "inputParameter",
      dtInstanceType: "template",
      sort_by: queryOptions?.sortModel?.[0]?.field,
      sort_order: queryOptions?.sortModel?.[0]?.sort,
      search,
      tag_id: selectedTag?.value as string
    });
  };

  const giveMeDropDownValues = () => {
    return {
      dropDownLabel: "Parameters Tag",
      showDropDown: true,
      dropDownOptions: tagsData.isLoading ? [allOption] : [allOption, ...(tagsData?.data?.tags ?? [])],
      onDropDownChange: (tag: SelectBoxOption) => setTag(tag),
      dropDownValue: selectedTag
    }
  }

  React.useEffect(() => {
    fetchInputParameters();
    // eslint-disable-next-line
  }, [paginationModel, JSON.stringify(queryOptions?.sortModel), selectedTag]);

  React.useEffect(() => {
    const timeout = setTimeout(() => {
      fetchInputParameters();
    }, 500)

    return () => clearTimeout(timeout)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search])

  return (
    (<MyPaper>
      <>
        <TitleHeader
          title="All Searches"
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder={"Search"}
          showCreateBtn={hasAccess}
          createBtnLabel={"Create Data Table"}
          onCreateBtn={() => viewInputParameter(null)}
          {...giveMeDropDownValues()}
        />

        {/* Error */}
        {isError && (
          <Alert sx={{ mt: 2 }} severity="error">
            Oops! Something went wrong, Unable to fetch Data Table. Try Again
            Later!
          </Alert>
        )}

        <Box
          sx={{
            mt: 2,
            height: "calc(100vh - 216px)"
          }}>
          <CustomDataGrid
            id="technical-parameter-list"
            rows={ROWS}
            columns={TableColumn(deleteInputParameter, viewInputParameter, duplicate, hasAccess, getPath, updatePublishStatus)}
            loading={loading}
            getRowId={(row) => row._id}
            showToolbar={true}
            rowCount={totalCount}
            paginationModel={paginationModel}
            paginationMode="server"
            onPaginationModelChange={setPaginationModel}
            sortingMode="server"
            onSortModelChange={handleSortModelChange}
          />
        </Box>
      </>
    </MyPaper>)
  );
};

export default InputParameterList;
